/**
 * Composición corporal (§5.3). Lógica compartida.
 *
 * El formulario de una medición no guarda directo: valida contra §8 y, cuando el salto de peso
 * supera 3 kg respecto de la lectura anterior, exige confirmación explícita. Una diferencia de
 * 9 kg de un día para otro casi siempre es un dígito mal leído, no una pérdida.
 */

import { useCallback, useMemo, useState } from 'react';
import {
  MIN_LECTURAS_COMPARABLES,
  alturaDesdeIMC,
  bandaFFMI,
  derivarMedida,
  masaMagraEnDescenso,
  pRatio,
  seriesComposicion,
  validarMedida,
  velocidadPerdida,
  type Condiciones,
  type Hallazgo,
  type MedidaCorporal,
  type Unidad,
} from '@mg/core';

import { guardarMedida } from './acciones.ts';
import { useDatos } from './useDatos.ts';

/**
 * La leyenda de un gráfico tiene que describir lo que se ve.
 *
 * `LineaConMedia` no dibuja línea con menos de 3 lecturas —dos puntos siempre forman una recta
 * perfecta— y en ese caso los puntos crudos quedan tapados por los sólidos. La frase de "la media
 * de 7 días y los puntos grises" describía entonces un gráfico que no estaba en la pantalla.
 */
export function leyendaDeSerie(lecturas: number): string {
  return lecturas < MIN_LECTURAS_COMPARABLES
    ? `Cada punto es una lectura suelta. La línea de tendencia aparece con ${MIN_LECTURAS_COMPARABLES}.`
    : 'La línea es la media de 7 días; los puntos grises, cada lectura suelta.';
}

/**
 * Una cifra que sale de una o dos lecturas NO es una tendencia y §5.3 prohíbe mostrarla como un
 * hecho. Devuelve la nota que tiene que acompañarla, o null cuando ya hay base para sostenerla.
 */
export function notaDePocasLecturas(lecturas: number): string | null {
  if (lecturas <= 0 || lecturas >= MIN_LECTURAS_COMPARABLES) return null;
  return `sale de ${lecturas} ${lecturas === 1 ? 'lectura' : 'lecturas'} — todavía no es tendencia`;
}

export interface Borrador {
  fecha: string;
  hora: string;
  peso: string;
  unidad_peso: Unidad;
  grasa_pct: string;
  masa_muscular: string;
  masa_magra: string;
  masa_osea: string;
  agua_pct: string;
  grasa_visceral: string;
  imc: string;
  tmb: string;
  condiciones: Condiciones;
}

const num = (s: string): number | null => {
  const t = s.trim().replace(',', '.');
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

export function borradorVacio(fecha: string): Borrador {
  return {
    fecha,
    hora: new Date().toTimeString().slice(0, 5),
    peso: '',
    unidad_peso: 'kg',
    grasa_pct: '',
    masa_muscular: '',
    masa_magra: '',
    masa_osea: '',
    agua_pct: '',
    grasa_visceral: '',
    imc: '',
    tmb: '',
    // 'desconocido' como default es deliberado: la app tiene que poder decir "no sé si esto es
    // comparable" en vez de asumir que sí.
    condiciones: 'desconocido',
  };
}

export function useCuerpo() {
  const datos = useDatos();
  const [borrador, setBorrador] = useState<Borrador>(() => borradorVacio(datos.hoy));
  const [confirmado, setConfirmado] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** La última medición guardada, para poder confirmarla con su cifra. */
  const [guardado, setGuardado] = useState<{ peso: number; unidad: Unidad } | null>(null);

  const escribir = useCallback((b: Borrador) => {
    // Volver a escribir descarta la confirmación anterior: la cifra confirmada es la que se
    // guardó, no la que se está tipeando encima.
    setBorrador(b);
    setGuardado(null);
  }, []);

  const descartarGuardado = useCallback(() => setGuardado(null), []);

  const altura = datos.perfil?.altura_cm ?? null;

  const composicion = useMemo(
    () => seriesComposicion(datos.medidas, altura, 7),
    [datos.medidas, altura],
  );

  const pratio = useMemo(
    () => pRatio(datos.medidas, altura, { ventanaDias: 28, hasta: datos.hoy }),
    [datos.medidas, altura, datos.hoy],
  );

  const velocidad = useMemo(
    () => velocidadPerdida(datos.medidas, { ventanaDias: 28, hasta: datos.hoy }),
    [datos.medidas, datos.hoy],
  );

  const tendenciaMagra = useMemo(
    () => masaMagraEnDescenso(datos.medidas, altura, 3),
    [datos.medidas, altura],
  );

  const ultima = datos.medidas[datos.medidas.length - 1] ?? null;
  const derivadaUltima = ultima ? derivarMedida(ultima, altura) : null;
  const banda =
    derivadaUltima?.ffmi != null ? bandaFFMI(derivadaUltima.ffmi, datos.perfil?.sexo ?? 'otro') : null;

  /** Altura despejada del IMC de la balanza, para ofrecerla a confirmar. */
  const alturaSugerida = useMemo(() => {
    if (altura !== null) return null;
    const conIMC = [...datos.medidas].reverse().find((m) => m.imc !== null && m.peso > 0);
    return conIMC?.imc ? alturaDesdeIMC(conIMC.peso, conIMC.imc) : null;
  }, [altura, datos.medidas]);

  /**
   * De dónde sale la altura sugerida, en una línea. Va acá y no en cada variante porque "de tu
   * IMC sale 179 cm" era circular —el IMC no se puede calcular sin altura— y la explicación no
   * puede divergir entre mobile y desktop.
   */
  const explicacionAltura = alturaSugerida
    ? `Tu balanza reporta un IMC; despejando la cuenta (peso ÷ altura²) sale ${alturaSugerida} cm. Confirmala vos.`
    : 'Se pide una sola vez.';

  const previa = useMemo(() => {
    const anteriores = datos.medidas.filter((m) => m.fecha < borrador.fecha);
    return anteriores[anteriores.length - 1] ?? null;
  }, [datos.medidas, borrador.fecha]);

  const validacion = useMemo(() => {
    const peso = num(borrador.peso);
    if (peso === null) {
      return { ok: false, requiereConfirmacion: false, hallazgos: [] as Hallazgo[] };
    }
    return validarMedida(
      {
        peso,
        unidad_peso: borrador.unidad_peso,
        grasa_pct: num(borrador.grasa_pct),
        masa_muscular: num(borrador.masa_muscular),
        masa_magra: num(borrador.masa_magra),
        masa_osea: num(borrador.masa_osea),
        agua_pct: num(borrador.agua_pct),
        grasa_visceral: num(borrador.grasa_visceral),
        imc: num(borrador.imc),
        tmb: num(borrador.tmb),
      },
      previa,
    );
  }, [borrador, previa]);

  const puedeGuardar =
    num(borrador.peso) !== null && validacion.ok && (!validacion.requiereConfirmacion || confirmado);

  /** Qué falta para poder guardar: un botón gris sin explicación es una pared. */
  const queFalta = useMemo((): string | null => {
    if (puedeGuardar) return null;
    if (num(borrador.peso) === null)
      return 'Falta el peso — es lo único obligatorio; el resto copialo de la balanza si lo muestra.';
    if (!validacion.ok) return 'Revisá el valor marcado acá arriba: así no se puede guardar.';
    if (validacion.requiereConfirmacion && !confirmado)
      return 'Marcá que el salto de peso es correcto para poder guardar.';
    return null;
  }, [puedeGuardar, borrador.peso, validacion, confirmado]);

  const guardar = useCallback(async (): Promise<MedidaCorporal | null> => {
    const peso = num(borrador.peso);
    if (peso === null || !puedeGuardar) return null;
    if (!datos.perfil) {
      // Sin perfil no hay a quién asociar la medida. Antes esto devolvía null en silencio y el
      // botón quedaba como si no hiciera nada.
      setError('Todavía no cargó tu perfil. Esperá un segundo y volvé a tocar Guardar medición.');
      return null;
    }

    setGuardando(true);
    setError(null);
    try {
      const medida = await guardarMedida({
        user_id: datos.perfil.id,
        fecha: borrador.fecha,
        hora: borrador.hora,
        peso,
        unidad_peso: borrador.unidad_peso,
        grasa_pct: num(borrador.grasa_pct),
        masa_muscular: num(borrador.masa_muscular),
        masa_magra: num(borrador.masa_magra),
        masa_osea: num(borrador.masa_osea),
        agua_pct: num(borrador.agua_pct),
        grasa_visceral: num(borrador.grasa_visceral),
        imc: num(borrador.imc),
        tmb: num(borrador.tmb),
        fuente: 'manual',
        condiciones: borrador.condiciones,
      });
      setBorrador(borradorVacio(datos.hoy));
      setConfirmado(false);
      setGuardado({ peso, unidad: borrador.unidad_peso });
      return medida;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo guardar.');
      return null;
    } finally {
      setGuardando(false);
    }
  }, [borrador, datos.perfil, datos.hoy, puedeGuardar]);

  return {
    datos,
    altura,
    alturaSugerida,
    explicacionAltura,
    composicion,
    /** Lecturas que sostienen cada serie: con menos de 3 no hay tendencia que mostrar (§5.3). */
    lecturasPeso: composicion.peso.length,
    lecturasMagra: composicion.magraKg.length,
    pratio,
    velocidad,
    tendenciaMagra,
    ultima,
    derivadaUltima,
    banda,
    borrador,
    setBorrador: escribir,
    previa,
    validacion,
    confirmado,
    setConfirmado,
    puedeGuardar,
    queFalta,
    guardar,
    guardando,
    guardado,
    descartarGuardado,
    error,
    derivadas: composicion.derivadas,
  };
}
